import { Tooltip, TooltipProps, useMergeRefs } from "@hightouch/htui";

import { usePermissionLock } from "./permission-lock-context";
import { V2PermissionProps, V2ResourceToPermission } from "./types-v2";
import {
  OrganizationPermissionInput,
  useOrganizationPermission,
} from "./use-organization-permissions";
import {
  ResourcePermissionInput,
  useResourcePermission,
} from "./use-resource-permission";
import { useContentFits } from "src/hooks/use-content-fits";
import { forwardRef } from "react";
import { isValidReactNode } from "src/utils/is-valid-react-node";

interface Props<V2Resource extends V2ResourceToPermission> {
  placement?: TooltipProps["placement"];
  permission?: ResourcePermissionInput<V2Resource>;
  organizationPermission?: OrganizationPermissionInput;
  /**
   * Tooltip for the component, always shown
   */
  tooltip?: string | boolean | React.ReactNode;
  /**
   * Tooltip to show in the event permissions are insufficient
   * @default "You do not have permission to perform this action"
   */
  unauthorizedTooltip?: string;
  /**
   * If the content overflows the container, this will show it in a tooltip.
   * Lowest tooltip priority.
   */
  showOverflowingContentAsTooltip?: boolean;
}

export function withPermission<
  P extends { isDisabled?: boolean },
  K extends keyof P,
>(
  WrappedComponent: React.ComponentType<P>,
  opts: {
    disableUnauthorizedTooltip?: boolean;
    /**
     * Prop to use for the content overflow tooltip.
     */
    contentOverflowTooltipPropKey?: K;
    /** Prop on element that accepts a ref to the element for the content overflow tooltip. */
    contentRefPropKey?: K;
  } = {
    disableUnauthorizedTooltip: false,
  },
) {
  // `Props` distributes over the resource union, so using the full
  // `V2ResourceToPermission` union here still enforces valid resource/grant
  // pairings at call sites. A generic render function no longer works with
  // `forwardRef` since @types/react 18.3 wraps its props in `PropsWithoutRef`,
  // a conditional type that cannot be resolved while `P` is still generic —
  // hence the render function cast below. Neither type has a `ref` prop, so
  // the two are equivalent once `P` is instantiated.
  const HOC = forwardRef<any, Props<V2ResourceToPermission> & Omit<P, "ref">>(((
    {
      permission,
      organizationPermission,
      tooltip,
      unauthorizedTooltip,
      showOverflowingContentAsTooltip,
      placement = "auto",
      ...props
    }: Props<V2ResourceToPermission> & Omit<P, "ref">,
    forwardedRef: React.Ref<any>,
  ) => {
    const resourcePermission = useResourcePermission(permission);
    const orgPermission = useOrganizationPermission(organizationPermission);
    const { isLocked: isLockedByContext, tooltip: contextLockTooltip } =
      usePermissionLock(permission);
    const { ref: contentFitsRef, contentFits } = useContentFits(
      showOverflowingContentAsTooltip,
      { skipClientWidthZero: true },
    );
    const { contentRefPropKey = "ref" } = opts;
    const ref = useMergeRefs(
      forwardedRef,
      contentRefPropKey === "ref" ? contentFitsRef : undefined,
    );
    const contentRef =
      contentRefPropKey !== "ref"
        ? { [contentRefPropKey]: contentFitsRef }
        : {};

    const hasPermissionCheck = !!permission || !!organizationPermission;
    const lookupIsPermitted = hasPermissionCheck
      ? permission
        ? resourcePermission.isPermitted
        : orgPermission
      : true;
    const isPermitted = isLockedByContext ? false : lookupIsPermitted;
    const permissionIsLoading =
      hasPermissionCheck && resourcePermission.isLoading;

    let tooltipMessage: React.ReactNode | undefined = undefined;
    let isTooltipDisabled: boolean | undefined = false;
    if (!isPermitted) {
      tooltipMessage =
        unauthorizedTooltip ??
        contextLockTooltip ??
        permissionTooltipCopy(permission, organizationPermission);
      isTooltipDisabled =
        permissionIsLoading || opts.disableUnauthorizedTooltip;
    } else if (tooltip) {
      tooltipMessage = tooltip;
    } else if (
      showOverflowingContentAsTooltip &&
      opts.contentOverflowTooltipPropKey
    ) {
      const value = (props as P)[opts.contentOverflowTooltipPropKey];
      if (isValidReactNode(value)) {
        tooltipMessage = value;
      }
      isTooltipDisabled = contentFits;
    }

    if (tooltipMessage) {
      return (
        <Tooltip
          isDisabled={isTooltipDisabled}
          message={tooltipMessage}
          placement={placement}
        >
          <WrappedComponent
            ref={ref}
            {...contentRef}
            {...(props as P)}
            isDisabled={!isPermitted || props.isDisabled}
          />
        </Tooltip>
      );
    }
    return <WrappedComponent ref={ref} {...contentRef} {...(props as P)} />;
  }) as React.ForwardRefRenderFunction<
    any,
    React.PropsWithoutRef<Props<V2ResourceToPermission> & Omit<P, "ref">>
  >);

  HOC.displayName = `WithPermission(${
    WrappedComponent?.displayName || WrappedComponent?.name || "Component"
  })`;

  return HOC;
}

const permissionTooltipCopy = (
  permission: ResourcePermissionInput<V2ResourceToPermission> | undefined,
  organizationPermission: OrganizationPermissionInput | undefined,
): string => {
  if (permission) {
    return resourcePermissionCopy(permission);
  }

  if (organizationPermission) {
    return organizationPermissionCopy(organizationPermission);
  }

  return "You do not have permission to perform this action";
};

const indefiniteArticle = (word: string): string =>
  /^[aeiou]/i.test(word) ? "an" : "a";

export const resourcePermissionCopy = (
  permission: ResourcePermissionInput<V2ResourceToPermission>,
): string => {
  const action = v2GrantAction[permission.v2.grant];
  const resource = permission.v2.resource;
  const hasId = "id" in permission.v2;

  const resourceToCopy = {
    idr: "IDR Graph",
    sync_sequence: "sync sequence",
  };

  const v2WorkspaceGroupPermission =
    permission.v2.grant.startsWith("can_create_") ||
    permission.v2.grant.startsWith("can_update_");

  // Native Delivery and AI Decisioning grants are workspace-scoped but have
  // specific action copy (e.g. "draft Native Delivery for") — surface that
  // instead of the generic workspace-admin message.
  if (
    resource === "workspace" &&
    (permission.v2.grant.includes("native_delivery") ||
      permission.v2.grant.includes("decision_engine"))
  ) {
    return `You do not have permission to ${action} this workspace`;
  }

  // Special case when the resource is a workspace, it helps make the action a little more clear
  return resource === "workspace" && !v2WorkspaceGroupPermission
    ? "You need to be a workspace admin to perform this action"
    : `You do not have permission to ${action} ${
        hasId ? "this" : indefiniteArticle(resourceToCopy[resource] ?? resource)
      } ${resourceToCopy[resource] ?? resource}`;
};

const organizationPermissionCopy = ({
  resource,
}: OrganizationPermissionInput): string => {
  return `You need to be ${
    resource === "organization" ? "an" : "a"
  } ${resource} admin to perform this action`;
};

const v2GrantAction: Record<
  V2PermissionProps<V2ResourceToPermission>["grant"],
  string
> = {
  can_read: "read",
  can_update: "update",
  can_delete: "delete",
  can_approve: "approve",
  can_preview: "preview",
  can_test: "test",
  can_debug: "debug",
  can_run: "run",
  can_access_data: "access data for",
  can_create: "create",
  can_create_sources: "create sources for",
  can_create_destinations: "create destinations for",
  can_create_event_sources: "create event sources for",
  can_create_event_warehouse_destinations:
    "create event warehouse destinations for",
  can_create_event_forwarding_destinations:
    "create event streaming destinations for",
  can_create_event_plans: "create event plans for",
  can_create_event_functions: "create event functions for",
  can_grant_support_access: "grant support access to",
  can_view_agents: "view agents for",
  can_configure_agents: "configure agents for",
  can_read_cep_consent: "read CEP consent for",
  can_update_cep_consent: "update CEP consent for",
  can_view_native_delivery: "view Native Delivery for",
  can_draft_native_delivery: "draft Native Delivery for",
  can_manage_native_delivery_messages: "manage Native Delivery messages for",
  can_manage_native_delivery_assets: "manage Native Delivery assets for",
  can_manage_native_delivery_channels: "manage Native Delivery channels for",
  can_manage_native_delivery_settings: "manage Native Delivery settings for",
  can_ingest_cep_push_tokens: "ingest CEP push tokens for",
  can_manage_decision_engine: "manage AI Decisioning for",
  can_edit_decision_engine_content: "edit AI Decisioning content for",
  is_workspace_admin: "manage",
  can_configure_journey: "configure journeys for",
  can_run_journey: "publish journeys for",
  can_delete_journey: "delete journeys for",
};
